import { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  IconButton,
  Button,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Link,
  AppBar,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  RegistrationRequest,
  Country,
  ExternalRegistrationRequest,
  AuthenticationResponse,
  ExternalAuthenticationType,
  ExternalAuthenticationRequest,
} from 'orderme-api-integration-client';
import { CustomerClient } from '../../../helpers/client';
import { openRules } from '../../policy/rulesHelper';
import { RegisterButton } from './registerStyles';
import { RegisterFieldValues, RegistrationProps } from '../../../interfaces/interfaces';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PhonePrefixSelection } from './phonePrefixSelection';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { AxiosResponse } from 'axios';
import { getStatusError } from '../../../helpers/errorMessage';
import { Gender, genderItems, getGenderLabel, getGenderType } from '../../../helpers/genderItems';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { useAppDispatch } from '../../../redux/hooks';
import { AppStyle } from '../../../helpers/appStyle';
import { appStyleMode } from '../../../helpers/clientConfigs';
import { BreadcrumbsBar } from '../../breadcrumbs/breadcrumbsBar';
import { ButtonLoadingIndicator } from '../../../controls';
import {
  authenticate,
  authenticateFromApple,
  authenticateFromFacebook,
  authenticateFromGoogle,
} from '../../../redux/reducers/authReducer';
import { NavBar } from '../../appBars/navBar/navBar';
import { isValidAge, isValidEmail, isValidPhoneNumber, MIN_AGE_REQUIREMENT } from '../../../helpers/validator';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';
import { RegistrationRoute } from '../../../routes/allRoutes';
import { FacebookSignIn } from '../externalLogin/facebookSignIn';
import { GoogleSignIn } from '../externalLogin/googleSignIn';
import { AppleSignIn } from '../externalLogin/appleSignIn';
import { markPhoneConfirmationRequired } from '@/helpers/sessionStorageService';

const inputValues = {
  firstName: '',
  lastName: '',
  birthDate: '',
  emailAddress: '',
  mobileNumber: '',
  gender: Gender.Unspecified,
  password: '',
  retypePsw: '',
};

export function Register() {
  const registrationProps = useLocation().state as RegistrationProps;

  if (registrationProps) {
    inputValues.firstName = registrationProps.firstName ?? '';
    inputValues.lastName = registrationProps.lastName ?? '';
    inputValues.emailAddress = registrationProps.emailAddress ?? '';
  }

  const isExternal = registrationProps !== null && registrationProps.externalLoginType !== null;
  const isEmailFromExternalLogin = isExternal && !!registrationProps.emailAddress;
  const routeNavigator = useRouteNavigator();
  const dispatch = useAppDispatch();
  const customersClient: CustomerClient = new CustomerClient();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [textFieldValues, setTextFieldValues] = useState<RegisterFieldValues>(inputValues);
  const [showPsw, setShowPsw] = useState(false);
  const [showRetypedPsw, setShowRetypedPsw] = useState(false);
  const [rulesApproved, setRulesApproved] = useState(false);
  const [communicationApproved, setCommunicationApproved] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>();
  const [openPrefixes, setOpenPrefixes] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState<string>(null);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [birthDateError, setBirthDateError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const passwordsMatch: boolean = textFieldValues.retypePsw === textFieldValues.password;

  const { t } = useTranslation(['account', 'gender']);

  const resetErrorMessage = useCallback(() => {
    setRegistrationError(null);
  }, []);

  const validate = useCallback(() => {
    let validValues = false;
    if (isExternal) {
      const { retypePsw: _, password: __, ...rest } = textFieldValues;
      validValues = Object.values(rest).every((value) => value !== '');
    } else {
      validValues = Object.values(textFieldValues).every((value) => value !== '');
    }

    const emailValid = isEmailFromExternalLogin || isEmailValid;

    if (validValues && rulesApproved && emailValid && !phoneError && passwordsMatch && !birthDateError) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [
    textFieldValues,
    rulesApproved,
    isEmailValid,
    isEmailFromExternalLogin,
    phoneError,
    passwordsMatch,
    birthDateError,
    isExternal,
  ]);

  const closePrefixes = useCallback(() => {
    setOpenPrefixes(false);
  }, []);

  const setCountryPrefix = useCallback(() => {
    if (!selectedCountry) {
      return;
    }

    const mobileNumberPrefix = { mobileNumber: selectedCountry.phonePrefix } as Pick<
      RegisterFieldValues,
      keyof RegisterFieldValues
    >;
    setTextFieldValues((prev) => ({
      ...prev,
      ...mobileNumberPrefix,
    }));
    closePrefixes();
  }, [selectedCountry, closePrefixes]);

  useEffect(() => {
    validate();
  }, [validate]);

  useEffect(() => {
    setCountryPrefix();
  }, [setCountryPrefix]);

  const validateBirthDate = (birthDate: string): string => {
    if (birthDate && !isValidAge(moment.utc(birthDate))) {
      return t('account:birthDateMinAgeError', { minAge: MIN_AGE_REQUIREMENT });
    }

    return '';
  };

  const handleFieldChange = (e: any) => {
    let { name, value } = e.target;

    if (name === 'emailAddress') {
      value = value?.trim();
    }

    if (name === 'birthDate') {
      const error = validateBirthDate(value);
      setBirthDateError(error);
    }

    if (name === 'mobileNumber') {
      validatePhone(value);
    }

    const fieldValue = { [name]: value } as Pick<RegisterFieldValues, keyof RegisterFieldValues>;
    setTextFieldValues({
      ...textFieldValues,
      ...fieldValue,
    });
  };

  const toggleApproveRules = () => {
    setRulesApproved(!rulesApproved);
  };

  const toggleApproveCommunication = () => {
    setCommunicationApproved(!communicationApproved);
  };

  useEffect(() => {
    validateEmail(textFieldValues.emailAddress);
  }, [textFieldValues.emailAddress]);

  const validateEmail = (mail: string) => {
    setIsEmailValid(isValidEmail(mail));
  };

  const validatePhone = (phone: string) => {
    if (!phone) {
      setPhoneError('');
      return;
    }

    const isValid = isValidPhoneNumber(phone);
    setPhoneError(isValid ? '' : t('account:phoneNumberInvalid'));
  };

  const validatePasswordStrength = useCallback(
    (password: string): string[] => {
      if (!password) {
        return [];
      }

      const errors: string[] = [];

      if (password.length < 8) {
        errors.push(t('account:passwordRule8Chars'));
      }
      if (!/[A-Z]+/.test(password)) {
        errors.push(t('account:passwordRuleCapitalLetter'));
      }
      if (!/[a-z]+/.test(password)) {
        errors.push(t('account:passwordRuleLowerCase'));
      }
      if (!/[0-9]+/.test(password)) {
        errors.push(t('account:passwordRuleNumber'));
      }

      return errors;
    },
    [t]
  );

  useEffect(() => {
    const errors = validatePasswordStrength(textFieldValues.password);
    setPasswordErrors(errors);
  }, [textFieldValues.password, validatePasswordStrength]);

  const registerUser = () => {
    // Validate birth date before registration
    const birthDateValidationError = validateBirthDate(textFieldValues.birthDate);
    if (birthDateValidationError) {
      setBirthDateError(birthDateValidationError);
      return;
    }

    // Validate phone number before registration
    if (!isValidPhoneNumber(textFieldValues.mobileNumber)) {
      setPhoneError(t('account:phoneNumberInvalid'));
      return;
    }

    setIsRegistering(true);

    if (isExternal) {
      let registrationRequest = {
        name: textFieldValues.firstName,
        surname: textFieldValues.lastName,
        email: isEmailFromExternalLogin ? undefined : textFieldValues.emailAddress,
        phone: textFieldValues.mobileNumber,
        gender: getGenderType(textFieldValues.gender),
        communicationAgreed: communicationApproved,
        rulesAcceptedAgreed: rulesApproved,
        credentials: registrationProps.externalAuthRequest.accessToken,
        code: registrationProps.externalAuthRequest.code,
        state: registrationProps.externalAuthRequest.state,
        birthDate: textFieldValues.birthDate ? moment.utc(textFieldValues.birthDate) : null,
      } as ExternalRegistrationRequest;

      customersClient
        .registerFromExternal(registrationRequest, registrationProps.externalLoginType)
        .then((response) => {
          if (response.errors) {
            var errKey: string = Object.keys(response.errors)[0];
            var errMsg = response.errors[errKey][0];
            setRegistrationError(errMsg);
            setIsRegistering(false);

            return;
          }

          authenticateFromExternal(
            registrationRequest.credentials,
            registrationRequest.state,
            registrationRequest.code
          );
        })
        .catch((err: AxiosResponse) => {
          var msg = getStatusError(err.status);
          setRegistrationError(msg);
          setIsRegistering(false);
          console.log(err);
        });
    } else {
      let registrationRequest = {
        name: textFieldValues.firstName,
        surname: textFieldValues.lastName,
        email: textFieldValues.emailAddress,
        phone: textFieldValues.mobileNumber,
        password: textFieldValues.password,
        gender: getGenderType(textFieldValues.gender),
        communicationAgreed: communicationApproved,
        rulesAcceptedAgreed: rulesApproved,
        birthDate: textFieldValues.birthDate ? moment.utc(textFieldValues.birthDate) : null,
      } as RegistrationRequest;

      customersClient
        .register(registrationRequest)
        .then((response) => {
          if (response.errors) {
            var errKey: string = Object.keys(response.errors)[0];
            var errMsg = response.errors[errKey][0];
            setRegistrationError(errMsg);
            setIsRegistering(false);

            return;
          }

          dispatch(authenticate({ input: registrationRequest.phone, password: registrationRequest.password }))
            .unwrap()
            .then(() => handleAuthenticationResponse())
            .catch((err) => {
              setIsRegistering(false);
              console.log(err);
            });
        })
        .catch((err: AxiosResponse) => {
          var msg = getStatusError(err.status);
          setRegistrationError(msg);
          setIsRegistering(false);
          console.log(err);
        });
    }
  };

  const authenticateFromExternal = (credentials: string, state: string, code: string) => {
    let externalPromise: Promise<AuthenticationResponse>;
    let externalAuthRequest = {
      accessToken: credentials,
      state: state,
      code: code,
    } as ExternalAuthenticationRequest;
    switch (registrationProps?.externalLoginType) {
      case ExternalAuthenticationType.Google:
        externalPromise = dispatch(authenticateFromGoogle(externalAuthRequest)).unwrap();
        break;
      case ExternalAuthenticationType.Apple:
        externalPromise = dispatch(authenticateFromApple(externalAuthRequest)).unwrap();
        break;
      case ExternalAuthenticationType.Facebook:
        externalPromise = dispatch(authenticateFromFacebook(externalAuthRequest)).unwrap();
        break;
      default:
        console.error('Invalid external login type: ' + registrationProps?.externalLoginType);
        return;
    }

    externalPromise
      .then(() => handleAuthenticationResponse())
      .catch((err) => {
        setIsRegistering(false);
        console.log(err);
      });
  };

  const handleAuthenticationResponse = () => {
    resetErrorMessage();
    setIsRegistering(false);
    markPhoneConfirmationRequired();
    routeNavigator.goHome(false, 'popup=confirm-phone');
  };

  const registerButton = () => {
    if (appStyleMode === AppStyle.Mobile) {
      return registerButtonMobile();
    }

    return registerButtonWebsite();
  };

  const registerButtonMobile = () => {
    return (
      <AppBar sx={{ backgroundColor: 'transparent', top: 'auto', bottom: '0px' }}>
        <RegisterButton
          sx={{ borderRadius: '4px' }}
          disabled={isButtonDisabled}
          variant="contained"
          onClick={() => registerUser()}
        >
          <Stack direction="row" justifyContent="center" alignItems="center">
            {isRegistering && (
              <CircularProgress sx={{ height: '20px', width: '20px', mr: '5px', color: 'progress.circular.primary' }} />
            )}
            <Typography>{t('account:confirmRegistration')}</Typography>
          </Stack>
        </RegisterButton>
      </AppBar>
    );
  };

  const registerButtonWebsite = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          sx={{
            backgroundColor: 'button.secondary.background',
            color: 'button.primary.color',
            width: '100%',
            maxWidth: '500px',
          }}
          disabled={isButtonDisabled || isRegistering}
          onClick={registerUser}
          loading={isRegistering}
          loadingIndicator={<ButtonLoadingIndicator />}
          variant="contained"
        >
          <Box>{t('account:confirmRegistration')}</Box>
        </Button>
      </Box>
    );
  };

  return (
    <Stack direction="column" alignItems="center" sx={{ width: '100%' }}>
      <NavBar barTitle={t('account:registration')} />
      <BreadcrumbsBar paths={[{ path: RegistrationRoute }]} />
      <Box
        className={appStyleMode === AppStyle.Mobile ? 'register-list' : null}
        sx={{
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '15px',
          color: 'black',
          mt: '10px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ m: '20px', maxWidth: '1000px' }}>
          <Grid
            container
            spacing={{ xs: 2, md: 3 }}
            sx={{
              width: '100%',
            }}
          >
            {appStyleMode !== AppStyle.Mobile && (
              <Grid size={12} sx={{ pt: '0px !important' }}>
                <Typography sx={{ fontSize: '24px' }}>{t('account:registration')}</Typography>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="firstName"
                label={t('account:name')}
                value={textFieldValues.firstName}
                onChange={handleFieldChange}
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '15px',
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="lastName"
                label={t('account:lastName')}
                value={textFieldValues.lastName}
                onChange={handleFieldChange}
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '15px',
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="emailAddress"
                label={t('account:email')}
                value={textFieldValues.emailAddress}
                onChange={handleFieldChange}
                disabled={isEmailFromExternalLogin}
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '15px',
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        {isEmailValid && textFieldValues.emailAddress !== '' && (
                          <CheckIcon sx={{ color: 'success.light', height: '20px', width: '20px' }} />
                        )}
                        {!isEmailValid && textFieldValues.emailAddress !== '' && (
                          <CloseIcon sx={{ color: 'error.main', height: '20px', width: '20px' }} />
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="mobileNumber"
                label={t('account:phoneNumberShort')}
                placeholder="+370xxxxxxxx"
                value={textFieldValues.mobileNumber}
                onChange={handleFieldChange}
                error={!!phoneError}
                helperText={phoneError}
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '15px',
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setOpenPrefixes(true)} edge="end">
                          {openPrefixes ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="gender"
                label={t('gender:gender')}
                value={textFieldValues.gender}
                onChange={handleFieldChange}
                select
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '15px',
                    },
                  },
                }}
              >
                {genderItems.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {getGenderLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="birthDate"
                label={t('account:birthDate')}
                value={textFieldValues.birthDate}
                onChange={handleFieldChange}
                type="date"
                error={!!birthDateError}
                helperText={birthDateError}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  input: {
                    sx: {
                      borderRadius: '15px',
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {!isExternal && (
                <TextField
                  fullWidth
                  name="password"
                  label={t('account:password')}
                  value={textFieldValues.password}
                  onChange={handleFieldChange}
                  type={showPsw ? 'text' : 'password'}
                  error={passwordErrors.length > 0 && textFieldValues.password !== ''}
                  helperText={
                    passwordErrors.length > 0 && textFieldValues.password !== '' ? (
                      <Box component="span">
                        <Typography variant="caption" component="div">
                          {t('account:passwordRules')}
                        </Typography>
                        {passwordErrors.map((error, index) => (
                          <Typography key={index} variant="caption" component="div">
                            • {error}
                          </Typography>
                        ))}
                      </Box>
                    ) : null
                  }
                  FormHelperTextProps={{
                    component: 'div',
                  }}
                  slotProps={{
                    input: {
                      sx: {
                        borderRadius: '15px',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPsw(!showPsw)} edge="end">
                            {showPsw ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {!isExternal && (
                <TextField
                  fullWidth
                  name="retypePsw"
                  label={t('account:retypeNewPassword')}
                  value={textFieldValues.retypePsw}
                  onChange={handleFieldChange}
                  type={showRetypedPsw ? 'text' : 'password'}
                  error={!passwordsMatch && textFieldValues.retypePsw !== ''}
                  helperText={
                    !passwordsMatch && textFieldValues.retypePsw !== '' ? t('account:differentPasswords') : null
                  }
                  slotProps={{
                    input: {
                      sx: {
                        borderRadius: '15px',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowRetypedPsw(!showRetypedPsw)} edge="end">
                            {showRetypedPsw ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            </Grid>
          </Grid>
          <Grid>
            <FormControlLabel
              control={<Checkbox checked={communicationApproved} onChange={toggleApproveCommunication} />}
              label={t('account:communicationApproved')}
            />
            <Stack direction="row" alignItems="center" sx={{ width: '100%', ml: '-10px' }}>
              <Checkbox checked={rulesApproved} onChange={toggleApproveRules} />
              <Box>
                <Typography display="inline" onClick={toggleApproveRules}>
                  {t('account:agreeWithTermsPart1')}
                </Typography>
                <Typography display="inline">
                  <Link
                    component="button"
                    underline="hover"
                    sx={{ verticalAlign: 'baseline' }}
                    onClick={() => openRules(routeNavigator.getRulesPath())}
                  >
                    {t('account:agreeWithTermsPart2Link')}
                  </Link>
                </Typography>
              </Box>
            </Stack>
          </Grid>
          {registrationError && (
            <Typography color="error" sx={{ marginBottom: '20px', fontSize: '17px', fontWeight: 'bold' }}>
              {registrationError}
            </Typography>
          )}
          {registerButton()}
          <Divider sx={{ fontSize: '18px', my: '20px' }}>{t('account:or')}</Divider>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
            <FacebookSignIn isVisible={true} />
            <GoogleSignIn isVisible={true} />
            <AppleSignIn isVisible={true} />
          </Stack>
          <PhonePrefixSelection
            open={openPrefixes}
            setSelectedCountry={setSelectedCountry}
            handleClose={closePrefixes}
          />
        </Box>
      </Box>
    </Stack>
  );
}
